import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { EngagementService } from "@services/engagements/engagementService";
import { findRunningPublish } from "@services/jobs/runningPublish";
import type { Job } from "@models/job.model";
import { NetworkViewPanel } from "./components/NetworkViewPanel";
import { CampaignPublishControl } from "./components/CampaignPublishControl";

/*
    Publish is scoped to the campaigns Ops selected (MPN-1364). The old button published the
    whole engagement, which forced a campaign waiting on approvals to hold up its siblings.

    "Publish as paused" is a separate button rather than a checkbox next to Publish. It is
    the difference between a campaign going live and not, and the ticket is explicit that it
    must not read as a routine default.

    While a publish is still running the buttons stay off and the panel says which job it is
    (MPN-2232). The server refuses a second publish anyway; this is so Ops sees the running
    job instead of a red error, because "0%" on a queued parent is what invited the click.
*/

const RUNNING_POLL_MS = 15_000;

export const PublishActions = () => {
  const engSnap = useSnapshot(engagementStore);

  const [publishLoading, setPublishLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [controlKey, setControlKey] = useState(0);
  const [selectedLaunchIds, setSelectedLaunchIds] = useState<number[]>([]);
  const [runningJob, setRunningJob] = useState<Job | null>(null);
  const lastPolled = engSnap.networkViewCacheDate as Date | null;
  const engagementId = engSnap.selectedEngagement.id;
  const engagementName = engSnap.selectedEngagement.name;

  // Checked on open and after each publish; re-checked every 15s only while one is running,
  // so the notice clears on its own and an idle panel costs nothing.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const job = await findRunningPublish(engagementId, engagementName);
        if (!cancelled) setRunningJob(job);
      } catch {
        // Without the jobs permission there is no notice; the server still refuses.
      }
    };
    check();
    const timer = runningJob ? setInterval(check, RUNNING_POLL_MS) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [engagementId, engagementName, controlKey, runningJob?.id]);

  const selectedCount = selectedLaunchIds.length;
  const canPublish = selectedCount > 0 && !publishLoading && !runningJob;

  async function publish(publishPaused: boolean) {
    setPublishLoading(true);
    setPublishError(null);
    try {
      await new EngagementService().publishEngagement(engSnap.selectedEngagement.id, {
        launchIds: selectedLaunchIds,
        publishPaused,
      });
      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 2000);
      // Clear the selection: the action is done, and leaving it ticked invites a second
      // press that would republish the same campaigns.
      setSelectedLaunchIds([]);
      // Last-published and history only change once the job runs, but re-reading now picks
      // up anything already recorded and costs one cheap local query.
      setControlKey((k) => k + 1);
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : "Publish failed");
      setTimeout(() => setPublishError(null), 4000);
    } finally {
      setPublishLoading(false);
    }
  }

  const publishIcon = publishSuccess
    ? "bi-check-circle icon-fade-in"
    : publishError
      ? "bi-x-circle icon-fade-in"
      : publishLoading
        ? "bi-arrow-clockwise icon-spin"
        : "bi-upload";

  const publishLabel = publishSuccess
    ? "Job created"
    : publishError
      ? "Failed"
      : publishLoading
        ? "Creating..."
        : `Publish selected (${selectedCount})`;

  return (
    <div className="nh-pane">
      <div className="nh-head">
        <i className="bi bi-cloud-arrow-up" style={{ color: "#64748b" }} />
        <span className="nh-head__title">Publish</span>
        {lastPolled && (
          <span className="nh-head__polled">Last polled {lastPolled.toLocaleTimeString()}</span>
        )}
        <button
          type="button"
          className="nh-head__ghost ms-auto"
          title="Refresh network view"
          onClick={() => setRefreshKey((k) => k + 1)}
        >
          <i className="bi bi-arrow-clockwise" />
          Refresh
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          style={{ fontSize: "0.72rem" }}
          onClick={() => publish(true)}
          disabled={!canPublish}
          title="Sync to the networks but leave everything paused"
        >
          <i className="bi bi-pause-circle me-1" />
          Publish as paused
        </button>
        <button
          type="button"
          className={`btn btn-sm ${publishSuccess ? "btn-success" : publishError ? "btn-danger" : "btn-primary"}`}
          style={{ fontSize: "0.72rem" }}
          onClick={() => publish(false)}
          disabled={!canPublish}
        >
          <i className={`bi ${publishIcon} me-1`} />
          {publishLabel}
        </button>
      </div>

      {publishError && <div className="nh-head__error">{publishError}</div>}
      {runningJob && (
        <div className="nh-head__notice" role="status">
          <i className="bi bi-hourglass-split me-1" />
          Publish job {runningJob.id} is {runningJob.status.toLowerCase()} since{" "}
          {new Date(runningJob.create_date).toLocaleTimeString()}. Publish again once it finishes.
        </div>
      )}

      <CampaignPublishControl
        engagementId={engSnap.selectedEngagement.id}
        selectedLaunchIds={selectedLaunchIds}
        onSelectionChange={setSelectedLaunchIds}
        refreshKey={controlKey}
      />

      <NetworkViewPanel refreshKey={refreshKey} />
    </div>
  );
};
